/**
 * What a floor can make, measured rather than asserted.
 *
 * A fab somebody built themselves (fab/floorPlan.ts) is a SUBSET of the
 * twenty-five stations, and the interesting question about any subset is not
 * how many tools it has - it is which operations have nowhere to run. That
 * question already has an answer in this directory: `routes.ts` holds the
 * product route as data, every step of it names the tool that runs it, and
 * `expand` flattens the whole thing including the ten times round the metal
 * loop. So coverage is an intersection, and nothing here has to know what a
 * fab is for.
 *
 * THIS IS THE POINT OF LETTING ANYONE DELETE A TOOL. "No CMP-11" is not a
 * missing icon, it is 30 operations with no tool to run on and no damascene
 * interconnect, which is why a power-discrete line uses aluminium and why
 * CMP-11 was the enabling tool for copper in the first place. The authoring
 * assistant says that sentence from these numbers, not from a table of
 * sentences somebody wrote.
 *
 * WHY THE REFERENCE IS A LOGIC ROUTE. Because it is the one the rest of the
 * app is built around and the only one with a full cost model behind it, and
 * because a floor scores against it honestly: an assembly-and-test floor
 * covers the back end and nothing else, and that is a true statement about an
 * OSAT rather than a bad mark. The segments are reported separately for
 * exactly that reason - the total is the least interesting number here.
 */

import { expand, type FlowStep, type ProcessFlow } from './model'
import { BACK_END, gateModule, METAL_LAYER, SINGLE_DUV, SUBSTRATE } from './routes'

/** Metal layers in the reference product. Ten, as `productRoute` has it. */
export const REFERENCE_METAL_LAYERS = 10

/**
 * The four stretches of a product route, each a thing a fab either does or
 * does not do. A real business is defined by which of these it owns: a
 * substrate supplier owns the first, a foundry the middle two, an OSAT the
 * last, and an IDM all four.
 */
export type SegmentId = 'substrate' | 'gate' | 'metal' | 'backEnd'

export const SEGMENT_IDS: readonly SegmentId[] = ['substrate', 'gate', 'metal', 'backEnd']

const segmentFlow = (): Record<SegmentId, ProcessFlow> => ({
  substrate: SUBSTRATE,
  gate: gateModule(SINGLE_DUV),
  metal: METAL_LAYER,
  backEnd: BACK_END,
})

/** How many times the wafer goes round each segment in one product. */
const SEGMENT_REPEAT: Record<SegmentId, number> = {
  substrate: 1,
  gate: 1,
  metal: REFERENCE_METAL_LAYERS,
  backEnd: 1,
}

/**
 * The station a step runs on.
 *
 * The EUV route runs on EUV-22, which is a station of its own: a floor
 * without it has no tool for an EUV exposure, and says so. (It used to be
 * `LITH-03-EUV`, a second price on the immersion scanner's bay, folded back
 * onto LITH-03 here - right while the floor had one scanner, wrong once it
 * had two.) What still folds is a VARIANT of a station: the High-NA scanner
 * is priced separately and stands in the same bay.
 */
export const stationTag = (step: FlowStep): string => step.tool.replace(/-HIGHNA$/, '')

export interface ToolGap {
  /** Asset tag, as the route names it. */
  tag: string
  /** Operations in the reference product that would run here. */
  steps: number
  /** How many of those need a reticle. */
  masks: number
}

export interface SegmentCoverage {
  id: SegmentId
  steps: number
  covered: number
  /** Tools this segment needs that the floor has not got, worst first. */
  missing: ToolGap[]
}

export interface FloorCoverage {
  /** Operations in the reference product, metal loop expanded. */
  steps: number
  covered: number
  masks: number
  masksCovered: number
  segments: SegmentCoverage[]
  /** Every absent tool with work in the route, worst first. */
  gaps: ToolGap[]
  /**
   * Stations on this floor the reference product never visits.
   *
   * NOT a criticism, and the assistant must not read it as one: GOWN-00,
   * FAC-06 and GAS-19 are here, and so is PAT-21, whose multiple-patterning
   * module is an alternative to the single exposure this reference uses.
   * A floor with no route work for a tool is a floor that bought it for
   * something this measurement does not measure.
   */
  unused: string[]
  /** True when every operation in the reference product has a tool. */
  complete: boolean
}

const tally = (gaps: Map<string, ToolGap>, tag: string, step: FlowStep): void => {
  const at = gaps.get(tag) ?? { tag, steps: 0, masks: 0 }
  at.steps += 1
  if (step.mask) at.masks += 1
  gaps.set(tag, at)
}

const worstFirst = (a: ToolGap, b: ToolGap): number => b.steps - a.steps || a.tag.localeCompare(b.tag)

/**
 * Run the reference product over a floor and see what has nowhere to go.
 *
 * `tags` is the asset tags standing on the floor - the caller's business,
 * because this module deliberately knows nothing about fab/layout.ts.
 */
export function coverageOf(tags: Iterable<string>): FloorCoverage {
  const floor = new Set(tags)
  const flows = segmentFlow()
  const gaps = new Map<string, ToolGap>()
  const used = new Set<string>()

  let steps = 0
  let covered = 0
  let masks = 0
  let masksCovered = 0

  const segments: SegmentCoverage[] = SEGMENT_IDS.map((id) => {
    const repeat = SEGMENT_REPEAT[id]
    const missing = new Map<string, ToolGap>()
    let segSteps = 0
    let segCovered = 0
    for (const step of expand(flows[id])) {
      const tag = stationTag(step)
      const here = floor.has(tag)
      segSteps += repeat
      steps += repeat
      if (step.mask) masks += repeat
      if (here) {
        used.add(tag)
        segCovered += repeat
        covered += repeat
        if (step.mask) masksCovered += repeat
      } else {
        for (let n = 0; n < repeat; n++) {
          tally(missing, tag, step)
          tally(gaps, tag, step)
        }
      }
    }
    return { id, steps: segSteps, covered: segCovered, missing: [...missing.values()].sort(worstFirst) }
  })

  return {
    steps,
    covered,
    masks,
    masksCovered,
    segments,
    gaps: [...gaps.values()].sort(worstFirst),
    unused: [...floor].filter((tag) => !used.has(tag)).sort(),
    complete: covered === steps,
  }
}

/** Covered share of the reference product, 0..1. */
export const coverageFraction = (c: FloorCoverage): number =>
  c.steps > 0 ? c.covered / c.steps : 0

/** Whether a segment runs end to end on this floor. */
export const segmentComplete = (s: SegmentCoverage): boolean => s.steps > 0 && s.covered === s.steps
